// File: src/App.js
/**
 * Main App Component for OSHA Code Viewer
 * Updated with integrated Header (no separate TopBar)
 *
 * Wrapped in LanguageProvider: one EN/ES/AR selection (switchable from the
 * header navigation) drives the app chrome strings AND the regulation-content
 * language in SectionViewer.
 */

import React, { useEffect, useRef, useState } from 'react';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import MainLayout from './components/layout/MainLayout';
import ContentArea from './components/layout/ContentArea';
import DisclaimerModal from './components/layout/DisclaimerModal';

// Navigation Components
import Sidebar from './components/navigation/Sidebar';
import BackToTop from './components/navigation/BackToTop';

// Content Components
import Breadcrumb from './components/content/Breadcrumb';
import SectionViewer from './components/content/SectionViewer';

// Search Components
import SearchBar from './components/search/SearchBar';
import SearchResults from './components/search/SearchResults';

// Action Components
import FAB from './components/actions/FAB';
import BottomBar from './components/actions/BottomBar';

// Language / UI strings
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { REGULATION_CARDS } from './i18n/strings';

// Deep links (?section= / ?reg= / ?search= / ?lang=) — shareable URLs used by
// cdrisk.com, the CDRisk employee/inspector apps, and sds.guide to link here.
import { parseDeepLink, buildShareUrl, syncUrl } from './utils/deepLink';

// CDRisk Account: password/passkey/social sign-in + annotation sync
import AccountModal from './components/account/AccountModal';
import { completeOAuthRedirect, getAccount } from './utils/account';
import { initAnnotationSync } from './utils/annotationSync';

// View state requested by the URL at load time (empty object on plain "/").
const INITIAL_LINK = parseDeepLink();

// All regulation parts available in the viewer (each has served EN + ES + AR
// data under public/data/<code>/). Order: core standards first, then
// admin/maritime/ag. Card copy per language lives in REGULATION_CARDS.
const REGULATION_CODES = [
  '1910', '1926', '1902', '1903', '1904', '1905',
  '1915', '1917', '1918', '1919', '1928',
];

function AppShell() {
  const { lang, setLang, t } = useLanguage();

  // State management (seeded from the URL so deep links open the right view)
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentRegulation, setCurrentRegulation] = useState(INITIAL_LINK.reg || '1910');
  const [currentSection, setCurrentSection] = useState(INITIAL_LINK.section || null);
  const [searchMode, setSearchMode] = useState(!!INITIAL_LINK.search);
  const [searchQuery, setSearchQuery] = useState(INITIAL_LINK.search || '');
  const [breadcrumbPath, setBreadcrumbPath] = useState(() =>
    INITIAL_LINK.section
      ? [
          { label: 'Home', key: 'home', url: '/' },
          { label: `29 CFR ${INITIAL_LINK.reg}`, url: `/?reg=${INITIAL_LINK.reg}` },
          { label: INITIAL_LINK.section }
        ]
      : [{ label: 'Home', key: 'home', url: '/' }]
  );

  // Breadcrumb labels are stored with a key where localizable so they follow
  // the language even after they were set.
  const localizeCrumb = (item) =>
    item.key === 'home' ? { ...item, label: t('nav.home') } : item;

  // ?lang= overrides the persisted choice for this visit (links from the
  // Spanish cdrisk.com pages arrive with lang=es).
  useEffect(() => {
    if (INITIAL_LINK.lang) setLang(INITIAL_LINK.lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the address bar in sync with the view so every screen is shareable
  // and browser back/forward work. popNav suppresses the echo write after a
  // popstate restore; view changes push history, in-place changes (typing a
  // query, switching language) replace it.
  const popNav = useRef(false);
  const lastSynced = useRef(null);

  useEffect(() => {
    const state = {
      reg: currentRegulation,
      section: currentSection || undefined,
      search: searchMode ? (searchQuery || '') : undefined,
      lang,
    };
    if (popNav.current) {
      popNav.current = false;
      lastSynced.current = state;
      return;
    }
    const prev = lastSynced.current;
    const sameView = prev &&
      prev.reg === state.reg &&
      prev.section === state.section &&
      (prev.search !== undefined) === (state.search !== undefined);
    syncUrl(state, { replace: !prev || sameView });
    lastSynced.current = state;
  }, [currentRegulation, currentSection, searchMode, searchQuery, lang]);

  useEffect(() => {
    const onPop = () => {
      const link = parseDeepLink();
      popNav.current = true;
      setCurrentRegulation(link.reg || '1910');
      setCurrentSection(link.section || null);
      setSearchMode(link.search !== undefined);
      setSearchQuery(link.search || '');
      setBreadcrumbPath(link.section
        ? [
            { label: 'Home', key: 'home', url: '/' },
            { label: `29 CFR ${link.reg}`, url: `/?reg=${link.reg}` },
            { label: link.section }
          ]
        : [{ label: 'Home', key: 'home', url: '/' }]);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Handle section selection
  const handleSectionClick = (section) => {
    setCurrentSection(section.code);
    setSearchMode(false);

    // Update breadcrumb
    setBreadcrumbPath([
      { label: 'Home', key: 'home', url: '/' },
      { label: `29 CFR ${currentRegulation}`, url: `/?reg=${currentRegulation}` },
      { label: section.code }
    ]);

    // Close sidebar on mobile
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  // Handle search from header
  const handleHeaderSearch = () => {
    setSearchMode(true);
    setCurrentSection(null);
  };

  // CDRisk Account (sign-in modal + cross-device annotation sync)
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountError, setAccountError] = useState('');
  const [signedIn, setSignedIn] = useState(() => !!getAccount());

  useEffect(() => {
    initAnnotationSync();
    // Returning from a Google/Microsoft/Apple redirect? Finish the sign-in.
    completeOAuthRedirect()
      .then((account) => {
        if (account) setAccountOpen(true); // show the signed-in confirmation
      })
      .catch((error) => {
        setAccountError(error.message);
        setAccountOpen(true);
      });

    const update = () => setSignedIn(!!getAccount());
    window.addEventListener('cdrisk:account-changed', update);
    return () => window.removeEventListener('cdrisk:account-changed', update);
  }, []);

  const handleLogin = () => {
    setAccountError('');
    setAccountOpen(true);
  };

  // Handle regulation card click
  const handleRegulationClick = (code) => {
    setCurrentRegulation(code);
    setSidebarOpen(true);
  };

  // Handle search result click
  const handleSearchResultClick = (result) => {
    // Results can come from any served regulation part
    const regulation = result.regulationCode || result.code.split('.')[0];

    setCurrentRegulation(regulation);
    setCurrentSection(result.code);
    setSearchMode(false);
    setSearchQuery('');

    setBreadcrumbPath([
      { label: 'Home', key: 'home', url: '/' },
      { label: `29 CFR ${regulation}`, url: `/?reg=${regulation}` },
      { label: result.code }
    ]);
  };

  // Handle back navigation
  const handleBack = () => {
    if (currentSection) {
      setCurrentSection(null);
      setBreadcrumbPath([{ label: 'Home', key: 'home', url: '/' }]);
    } else if (searchMode) {
      setSearchMode(false);
      setSearchQuery('');
    }
  };

  // Get dynamic title for header
  const getPageTitle = () => {
    if (currentSection) {
      return `§ ${currentSection}`;
    } else if (searchMode) {
      return t('title.search');
    } else {
      return `29 CFR ${currentRegulation}`;
    }
  };

  const cards = REGULATION_CARDS[lang] || REGULATION_CARDS.en;

  return (
    <div className="app">
      {/* Integrated Header (includes TopBar functionality + language switch) */}
      <Header
        onSearchClick={handleHeaderSearch}
        onLoginClick={handleLogin}
        loginLabel={signedIn ? 'Account' : undefined}
        title={getPageTitle()}
        showBackButton={!!currentSection || searchMode}
        onBackClick={handleBack}
        showSidebarToggle={!searchMode}
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      <AccountModal
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
        initialError={accountError}
      />

      {/* Main Content Area */}
      <MainLayout
        topBar={null}
        sidebar={
          <Sidebar
            regulationCode={currentRegulation}
            currentSection={currentSection}
            onSectionClick={handleSectionClick}
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
          />
        }
        content={
          <ContentArea>
            {searchMode ? (
              // Search Mode
              <>
                <SearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                  onSearch={setSearchQuery}
                  onClear={() => {
                    setSearchQuery('');
                    setSearchMode(false);
                  }}
                  autoFocus={true}
                />
                <SearchResults
                  query={searchQuery}
                  onResultClick={handleSearchResultClick}
                />
              </>
            ) : currentSection ? (
              // Section Viewing Mode
              <>
                <Breadcrumb
                  path={breadcrumbPath.map(localizeCrumb)}
                  onNavigate={(item) => {
                    if (item.url === '/') {
                      handleBack();
                    } else if (item.url && item.url.startsWith('/?reg=')) {
                      // Part-level crumb: back to browsing that part
                      setCurrentSection(null);
                      setBreadcrumbPath([{ label: 'Home', key: 'home', url: '/' }]);
                      setSidebarOpen(true);
                    }
                  }}
                />
                <SectionViewer
                  regulationCode={currentRegulation}
                  sectionCode={currentSection}
                  onRelatedClick={(code) => {
                    setCurrentSection(code);
                  }}
                />
              </>
            ) : (
              // Home/Browse Mode
              <div className="home-view" lang={lang} dir={lang === 'ar' ? 'rtl' : 'ltr'}>
                <h1>{t('home.title')}</h1>
                <p>{t('home.tagline')}</p>

                <div className="regulation-cards">
                  {REGULATION_CODES.map((code) => (
                    <div
                      key={code}
                      className="regulation-card"
                      onClick={() => handleRegulationClick(code)}
                    >
                      <h2><bdi dir="ltr">29 CFR {code}</bdi></h2>
                      <p>{(cards[code] || REGULATION_CARDS.en[code]).desc}</p>
                      <span className="card-badge">{(cards[code] || REGULATION_CARDS.en[code]).badge}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </ContentArea>
        }
        backToTop={<BackToTop hasFAB={!searchMode} />}
        sidebarOpen={sidebarOpen}
        onSidebarToggle={setSidebarOpen}
      />

      {/* Floating Action Button for Search */}
      {!searchMode && (
        <FAB
          onClick={() => setSearchMode(true)}
          hideOnScroll={true}
          className={currentSection ? 'fab-above-bottombar' : ''}
        />
      )}

      {/* Bottom Action Bar (shown when viewing a section) */}
      {currentSection && !searchMode && (
        <BottomBar
          section={{
            code: currentSection,
            title: `Section ${currentSection}`
          }}
          onShare={() => {
            // Canonical deep link for this section (BottomBar already tried
            // navigator.share; this fallback copies the link instead)
            const url = buildShareUrl({ reg: currentRegulation, section: currentSection, lang });
            if (navigator.clipboard?.writeText) {
              navigator.clipboard.writeText(url);
            } else if (navigator.share) {
              navigator.share({ title: `OSHA ${currentSection}`, url });
            }
          }}
          onPDF={() => {
            // PDF export functionality
            window.print();
          }}
          onNote={() => {
            // Note functionality
            console.log('Add note for', currentSection);
          }}
        />
      )}

      {/* Universal Footer */}
      <Footer />

      {/* One-time liability notice (shows on first visit only) */}
      <DisclaimerModal />
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AppShell />
    </LanguageProvider>
  );
}

export default App;
